import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../services/auth.service';
import { EnvironmentService } from '../../services/environment.service';
import { LocalizationService } from '../../services/localization.service';
import { PersistenceService } from '../../services/persistence.service';
import { Subscription } from 'rxjs';
import { PgAcceptConditionsComponent } from '../pg-accept-conditions/pg-accept-conditions.component';
import { PGUtilities } from '../../utilities/pg-utilities';

export type PgLoginHandlerMode = 'default'|'otp'|'code';

@Component({
    selector: 'app-pg-login-handler',
    templateUrl: './pg-login-handler.component.html',
    styleUrls: ['./pg-login-handler.component.scss'],
    standalone: false
})
export class PgLoginHandlerComponent implements OnInit, OnDestroy {

    @Input() customMessage:string;
    @Input() otpMode:boolean;
    @Input() codeMode:boolean;

    @Input() canDismiss:boolean;
    @Output() loginDismissed = new EventEmitter<void>();

    availableModes:Array<PgLoginHandlerMode> = ['default'];
    currentMode:PgLoginHandlerMode = 'default';

    private _subscriptionList:Array<Subscription> = [];

    constructor(private authService:AuthService, private environmentService:EnvironmentService, private modalService:NgbModal, private localizationService:LocalizationService, private persistenceService:PersistenceService) { }

    ngOnInit(): void {
        if(this.otpMode) {
            this.availableModes.push('otp')
        }

        if(this.codeMode) {
            if(this.environmentService.environment.LoginCodePath != null && this.environmentService.environment.LoginCodePath != '') {
                this.availableModes.push('code')
            }
        }

        this._subscriptionList.push(this.authService.statusChange.subscribe(() => {
            this.handleLoginStatus();  
        }))

        setTimeout(() => {
            this.handleLoginStatus();  
        }, 10)

        this._subscriptionList.push(this.localizationService.currentLanguageChange.subscribe(() => {
            this._dismissAllModals()
            
            setTimeout(() => {
                this.handleLoginStatus();
            }, 10)
        }))
    }

    private _dismissAllModals() {
        if(this._loginModalRef != null) {
            this._loginModalRef.dismiss();
            this._loginModalRef = null;
        }
        
        if(this._acceptModalRef != null) {
            this._acceptModalRef.dismiss();
            this._acceptModalRef = null;
        }

        if(this._realmErrorModalRef != null) {
            this._realmErrorModalRef.dismiss();
            this._realmErrorModalRef = null;
        }
    }

    ngOnDestroy(): void {
        for(let _subscription of this._subscriptionList) {
            _subscription.unsubscribe();
        }
        
        this._dismissAllModals();
    }

    isLoggedIn = false;

    isLoading = false;
    isResetting = false;

    @ViewChild('loginTemplate') loginTemplate: ElementRef;
    @ViewChild('realmErrorTemplate') realmErrorTemplate: ElementRef;

    private _loginModalRef:NgbModalRef = null;
    private _acceptModalRef:NgbModalRef = null;
    private _realmErrorModalRef:NgbModalRef = null;

    handleLoginStatus() {
        this.isLoggedIn = this.authService.isLoggedIn(true)

        if(!this.isLoggedIn) {
            if(this._loginModalRef == null) {
                let _savedObj = PGUtilities.tryParseJSON(this.persistenceService.getItem('PGLoginStatus'))

                if(_savedObj != null) {
                    if(this.availableModes.indexOf(_savedObj.mode) != -1) {
                        this.currentMode = _savedObj.mode;
                    }
                }

                let _cOptions:NgbModalOptions = {
                    backdrop: 'static',
                    keyboard: !!this.canDismiss,
                    windowClass: 'PGLoginHandlerModal',
                    size: 'lg',
                    ariaLabelledBy: 'pg-login-handler-title'
                }
    
                this._loginModalRef = this.modalService.open(this.loginTemplate, _cOptions);
                this._loginModalRef.result.then(() => {
                    this._loginModalRef = null;
                }, () => {
                    this._loginModalRef = null;
                })
            }
        }
        else {
            if(this._loginModalRef != null) {
                this._loginModalRef.dismiss();
            }

            this._checkForceRealm()
        }
    }

    setCurrentMode(val:PgLoginHandlerMode) {
        this.currentMode = val;
        this.userRequest = null;
    }

    isRequestingRealm = false;

    private _checkForceRealm() {
        if(!this.isRequestingRealm) {
            if(this.environmentService.environment.ForceRealm != null && this.environmentService.environment.ForceRealm != this.authService.user.realm.id) {
                let _isUnavailable = false;

                for(let _realm of this.authService.user.unavailableRealms) {
                    if(_realm.id == this.environmentService.environment.ForceRealm) {
                        _isUnavailable = true;
                        break;
                    }
                }

                if(_isUnavailable) {
                    this._showRealmError()
                }
                else {
                    let _isSelectable = false;

                    for(let _realm of this.authService.user.getSelectableRealms()) {
                        if(_realm.id == this.environmentService.environment.ForceRealm) {
                            _isSelectable = true;
                            break;
                        }
                    }

                    this.isRequestingRealm = true;

                    if(_isSelectable) {
                        this.authService.setUserRealm(this.environmentService.environment.ForceRealm).then(() => {
                            this.isRequestingRealm = false;
                            this._checkAcceptConditions();
                        }, () => {
                            this.isRequestingRealm = false;
                            this._showRealmError();
                        })
                    }
                    else {
                        this.authService.requestAddRealm(this.environmentService.environment.ForceRealm).then(() => {
                            this.authService.setUserRealm(this.environmentService.environment.ForceRealm).then(() => {
                                this.isRequestingRealm = false;
                                this._checkAcceptConditions();
                            }, () => {
                                this.isRequestingRealm = false;
                                this._showRealmError();
                            })
                        }, () => {
                            this.isRequestingRealm = false;
                            this._showRealmError();
                        })
                    }
                }
            }
            else {
                this._checkAcceptConditions();
            }
        }
    }

    private _checkAcceptConditions() {
        // Avoid dismiss+reopen when handleLoginStatus runs twice in a row
        // (e.g. statusChange + languageChange/setTimeout during auth init)
        if(this._acceptModalRef != null) {
            return;
        }

        if(!this.isRealmPendingAndDenied() && !this.authService.skipAcceptConditions) {
            let _realmId = this.authService.user.realm.id;

            // null realm: skip privacy/terms acceptance
            if(_realmId != null && (!this.authService.hasAcceptedPrivacyPolicy(_realmId) || !this.authService.hasAcceptedTermsAndConditions(_realmId))) {
                this._acceptModalRef = this.modalService.open(PgAcceptConditionsComponent, { size: 'lg' })
    
                let _component = this._acceptModalRef.componentInstance as PgAcceptConditionsComponent;
                _component.acceptTitle = this.authService.user.realm.name;
                _component.conditionList = [
                    { title: 'pg-footer.privacy-policy', text: this.localizationService.getPrivacyPolicyString(_realmId) },
                    { title: 'pg-footer.terms-and-conditions', text: this.localizationService.getTermsAndConditionsString(_realmId) },
                ]
    
                this._acceptModalRef.result.then(() => {
                    this._acceptModalRef = null;
                    this.authService.accept(_component.conditionList[0].accepted, _component.conditionList[1].accepted, _realmId).then(() => {
                        
                    }, () => {})
                }, () => {
                    this._acceptModalRef = null;
                })
            }
        }
    }

    private _showRealmError() {
        if(this._realmErrorModalRef == null) {
            let _cOptions:NgbModalOptions = {
                backdrop: 'static',
                keyboard: false,
                windowClass: 'PGLoginHandlerModal',
                size: 'lg'
            }

            this._realmErrorModalRef = this.modalService.open(this.realmErrorTemplate, _cOptions);
        }
    }

    isRealmPendingAndDenied() {
        let _user = this.authService.user;

        if(_user?.realm != null && _user?.realm.name != 'null') {
            return (_user.pendingRealms.indexOf(_user.realm) != -1 && _user.realm.configuration.denyPendingAccess)   
        }
    }

    getCurrentRealmName() {
        return this.authService.user?.realm?.name
    }

    resetLoginFields() {          
        this.isResetting = true;

        setTimeout(() => {
            this.isResetting = false;
        }, 10)
    }

    dismissLogin() {
        if(this.canDismiss) {
            if(this._loginModalRef != null) {
                this._loginModalRef.dismiss();
            }

            this.loginDismissed.emit()
        }
    }

    userRequest:string = null;

    onSetUserRequest(val:string) {
        this.userRequest = val;
    }
}
